import { useCallback, useEffect, useState } from "react";

const HOUR_MS = 60 * 60 * 1000;

export const BACKEND_PAYMENT_WINDOW_HOURS = 96;

export const PAYMENT_UI_FAKE_TOTAL_HOURS = 42;

export const PAYMENT_UI_POPUP_AFTER_REAL_HOURS = 36;

export const PAYMENT_UI_FAKE_EXTEND_HOURS = 48;

export const PAYMENT_UI_WARNING_HOURS = 36;

const LS_EXTEND = (matchId: string) => `gt_pay_ui_extend:${matchId}`;
const LS_MODAL = (matchId: string) => `gt_pay_ui_modal:${matchId}`;

export function getPaymentWindowStartMs(paymentDeadline: Date | string): number {
  return new Date(paymentDeadline).getTime() - BACKEND_PAYMENT_WINDOW_HOURS * HOUR_MS;
}

export function getStoredUiExtendMs(matchId: string): number {
  try {
    const v = localStorage.getItem(LS_EXTEND(matchId));
    if (!v) return 0;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export function addStoredUiExtendMs(matchId: string, ms: number) {
  const next = getStoredUiExtendMs(matchId) + ms;
  localStorage.setItem(LS_EXTEND(matchId), String(next));
}

export function getExtendModalHandled(matchId: string): boolean {
  return localStorage.getItem(LS_MODAL(matchId)) === "1";
}

export function setExtendModalHandled(matchId: string) {
  localStorage.setItem(LS_MODAL(matchId), "1");
}

export function getDisplayedPaymentMsRemaining(
  paymentDeadline: Date | string | null | undefined,
  matchId: string | null | undefined,
): number | null {
  if (!paymentDeadline || !matchId) return null;
  const realMs = new Date(paymentDeadline).getTime() - Date.now();
  if (realMs <= 0) return null;
  const scale = PAYMENT_UI_FAKE_TOTAL_HOURS / BACKEND_PAYMENT_WINDOW_HOURS;
  return realMs * scale + getStoredUiExtendMs(matchId);
}

export function formatPaymentHoursLabel(displayMs: number): string {
  const hours = Math.ceil(displayMs / HOUR_MS);
  return `${hours} ${hours === 1 ? "hour" : "hours"}`;
}

export function shouldOfferPaymentExtendModal(
  paymentDeadline: Date | string | null | undefined,
  matchId: string | null | undefined,
): boolean {
  if (!paymentDeadline || !matchId) return false;
  if (getExtendModalHandled(matchId)) return false;
  const realMs = new Date(paymentDeadline).getTime() - Date.now();
  if (realMs <= 0) return false;
  const elapsed = Date.now() - getPaymentWindowStartMs(paymentDeadline);
  return elapsed >= PAYMENT_UI_POPUP_AFTER_REAL_HOURS * HOUR_MS;
}

export function isPaymentUiWarningUrgent(displayMs: number | null): boolean {
  return displayMs != null && displayMs > 0 && displayMs <= PAYMENT_UI_WARNING_HOURS * HOUR_MS;
}

export function usePaymentWindowUiDemo(
  matchId: string | null | undefined,
  paymentDeadline: Date | string | null | undefined,
  isPaymentPhase: boolean,
) {
  const [modalOpen, setModalOpen] = useState(false);
  const [, setTick] = useState(0);

  const bump = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!isPaymentPhase || !matchId || !paymentDeadline) return;
    const run = () => {
      setTick((t) => t + 1);
      if (shouldOfferPaymentExtendModal(paymentDeadline, matchId)) {
        setModalOpen(true);
      }
    };
    run();
    const id = setInterval(run, 5_000);
    return () => clearInterval(id);
  }, [isPaymentPhase, matchId, paymentDeadline]);

  const displayMs =
    isPaymentPhase && matchId && paymentDeadline
      ? getDisplayedPaymentMsRemaining(paymentDeadline, matchId)
      : null;

  const handleExtend = useCallback(() => {
    if (!matchId) return;
    addStoredUiExtendMs(matchId, PAYMENT_UI_FAKE_EXTEND_HOURS * HOUR_MS);
    setExtendModalHandled(matchId);
    setModalOpen(false);
    bump();
  }, [matchId, bump]);

  const handleModalDismiss = useCallback(() => {
    if (!matchId) return;
    setExtendModalHandled(matchId);
    setModalOpen(false);
  }, [matchId]);

  return {
    displayMs,
    modalOpen,
    handleExtend,
    handleModalDismiss,
  };
}
